import React, { FormEvent, useState } from "react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { IgAlert } from "@intelligems/ig-theme/IgAlert";
import {
  CircularProgress,
  FormControl,
  InputAdornment,
  OutlinedInput,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "@src/store";
import { theme } from "@src/theme";

import { useShopifyLogin } from "../../api/shopify-callbacks/shopify-login";
import {
  removeOrg,
  selectedIsShopLogin,
  setEmailLogin,
  setShopLogin,
} from "../../store/slices/appSettings";
import { LoggedOrg } from "../../store/types/appSettings";

const PREFIX = "ShopLogin";

const classes = {
  paper: `${PREFIX}-paper`,
  paperHover: `${PREFIX}-paperHover`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.paper}`]: {
    minHeight: "100px",
    padding: "0px 10px 10px 10px",
  },
  [`& .${classes.paperHover}`]: { padding: "10px" },
}));

export const ShopLogin = () => {
  const isShopLogin = useSelector(selectedIsShopLogin);
  const loggedOrgs = useSelector((state) => state.appSettings.loggedOrgs);
  const dispatch = useDispatch();

  const shopifyLogin = useShopifyLogin();

  const [shopIdInput, setShopIdInput] = useState<string>("");
  const [loggingInShopId, setLoggingInShopId] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    severity: "error" | "info";
    message: string;
  } | null>(null);

  const handleSubmit = (shopId: string, event?: FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }

    if (shopId.length === 0) {
      setMessage({ severity: "error", message: "Invalid Shopify Store Name" });
      return;
    }

    setMessage(null);
    setLoggingInShopId(shopId);

    shopifyLogin.mutate(shopId, {
      onSuccess: (data) => {
        setMessage({
          severity: "info",
          message: `Redirecting to ${shopId}...`,
        });
        if (data.type === "redirect") {
          // eslint-disable-next-line react-compiler/react-compiler
          window.location.href = data.redirect + "&src=shopLoginOnSuccess";

          setTimeout(() => {
            setLoggingInShopId(null);
            setMessage({
              severity: "error",
              message: `Automatic redirect failed. Please try again.`,
            });
          }, 10000);
        }
      },
      onError: () => {
        setLoggingInShopId(null);
        dispatch(removeOrg(shopId));
        setMessage({
          severity: "error",
          message: `We could not log in to ${shopId}. Are you sure this store has Intelligems installed?`,
        });
      },
    });
  };

  const isDisabled = loggingInShopId !== null || shopifyLogin.isPending;

  return (
    <StyledGrid container direction={"column"} rowSpacing={2} mt={1}>
      {message && (
        <Grid sx={{ paddingRight: "5px" }}>
          <IgAlert {...message} alertKey="shop-login-error" />
        </Grid>
      )}
      <Grid
        container
        direction={"column"}
        rowSpacing={2}
        wrap={"nowrap"}
        sx={{ maxHeight: { xs: "200px", sm: "300px" }, overflowY: "scroll" }}
      >
        {loggedOrgs.map((o: LoggedOrg) => {
          const isLoggingIntoThisOrg = loggingInShopId === o.shopId;
          return (
            <Grid key={o.shopId} size="grow">
              <IgPaper
                isClickable={!isDisabled}
                className={classes.paperHover}
                variant={"outlined"}
                onClick={() => !isDisabled && handleSubmit(o.shopId)}
                sx={{
                  borderColor: isLoggingIntoThisOrg
                    ? theme.igBorders.info.active
                    : undefined,
                  borderWidth: isLoggingIntoThisOrg ? 2 : undefined,
                  minHeight: 44,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid>
                    <IgTypography variant="body1">{o.name}</IgTypography>
                  </Grid>
                  {isLoggingIntoThisOrg && (
                    <Grid>{<CircularProgress size={16} />}</Grid>
                  )}
                </Grid>
              </IgPaper>
            </Grid>
          );
        })}
      </Grid>
      <Grid mt={2} size="grow">
        {
          <IgPaper className={classes.paper}>
            <form onSubmit={(event) => handleSubmit(shopIdInput, event)}>
              <Grid
                container
                direction={"column"}
                justifyContent={"flex-start"}
                alignItems={"center"}
                spacing={2}
              >
                <Grid mt={2} size="grow">
                  <IgTypography variant={"body1"}>
                    {`Don't see your store? Sign in below:`}
                  </IgTypography>
                </Grid>
                <Grid style={{ width: "100%" }} size="grow">
                  <FormControl style={{ width: "100%" }}>
                    <OutlinedInput
                      id="manual-shop-id-input"
                      placeholder="my-awesome-shop"
                      value={shopIdInput}
                      onChange={(e) => setShopIdInput(e.target.value)}
                      startAdornment={
                        <InputAdornment position="end">https://</InputAdornment>
                      }
                      endAdornment={
                        <InputAdornment position="end">
                          .myshopify.com
                        </InputAdornment>
                      }
                      sx={{
                        fontSize: 16,
                        minHeight: 44,
                        "& input": { fontSize: 16 },
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid style={{ width: "100%" }} size="grow">
                  <IgButton
                    loading={isDisabled}
                    variant="contained"
                    fullWidth
                    type="submit"
                    size="medium"
                    sx={{
                      minHeight: 44,
                      padding: (theme) => theme.spacing(1.5, 2),
                    }}
                  >
                    Sign In
                  </IgButton>
                </Grid>
              </Grid>
            </form>
          </IgPaper>
        }
        <Grid mt={2} style={{ width: "100%" }} size="grow">
          <IgButton
            fullWidth
            size={"small"}
            variant={"text"}
            disabled={isDisabled}
            onClick={() =>
              dispatch(isShopLogin ? setEmailLogin() : setShopLogin())
            }
            sx={{ minHeight: 44, padding: (theme) => theme.spacing(1.5, 2) }}
          >
            Log in with Username / Password
          </IgButton>
        </Grid>
      </Grid>
    </StyledGrid>
  );
};
